import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRedemptionData, useRedemptionHistory, useApproveRedemption } from '../hooks/useRedemptionData';
import RedemptionInfoCard from '../components/redemption/RedemptionInfoCard';
import BankDetailsCard from '../components/redemption/BankDetailsCard';
import RedemptionHistoryTable from '../components/redemption/RedemptionHistoryTable';
import NotificationToast from '../components/common/NotificationToast';

const RedemptionSubscriberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Use custom hooks for data management
  const {
    profile,
    bank,
    redemptions: initialRedemptions,
    redemptionPagination: initialPagination,
    loading,
    error,
    currentRole,
    redemptionInitialLoadComplete,
    setRedemptions,
    setRedemptionPagination
  } = useRedemptionData(id);
  
  // Redemption search and pagination state
  const [redemptionSearch, setRedemptionSearch] = useState('');
  const [redemptionSearchInput, setRedemptionSearchInput] = useState('');
  const [redemptionStatus, setRedemptionStatus] = useState('');
  const [redemptionPage, setRedemptionPage] = useState(1);
  const redemptionSearchTimeoutRef = useRef(null);
  
  // UI state
  const [showAccount, setShowAccount] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Load redemption history with search
  const { redemptions: searchRedemptions, redemptionPagination: searchPagination } = useRedemptionHistory(
    id,
    currentRole,
    redemptionPage,
    redemptionSearch,
    redemptionInitialLoadComplete,
    redemptionStatus
  );

  // Use search results if available, otherwise use initial data
  const redemptions = redemptionSearch || redemptionStatus || redemptionPage > 1 ? searchRedemptions : initialRedemptions;
  const redemptionPagination = redemptionSearch || redemptionStatus || redemptionPage > 1 ? searchPagination : initialPagination;
  
  // Approve redemption functionality
  const { handleApproveRequest, approvingRequests, approvedRequestIds } = useApproveRedemption(
    id,
    currentRole,
    redemptionPage,
    redemptionSearch,
    setRedemptions,
    setRedemptionPagination
  );
  
  useEffect(() => setMounted(true), []);
  
  // Show notification helper
  const showNotification = useCallback((message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 5000);
  }, []);
  
  // Handle redemption search submission (Enter key or debounce)
  const handleRedemptionSearchSubmit = useCallback(() => {
    const newSearch = redemptionSearchInput.trim();
    if (newSearch !== redemptionSearch) {
      setRedemptionSearch(newSearch);
      setRedemptionPage(1);
    }
    if (redemptionSearchTimeoutRef.current) {
      clearTimeout(redemptionSearchTimeoutRef.current);
      redemptionSearchTimeoutRef.current = null;
    }
  }, [redemptionSearchInput, redemptionSearch]);

  // Debounce redemption search input (2 seconds)
  useEffect(() => {
    if (redemptionSearchTimeoutRef.current) {
      clearTimeout(redemptionSearchTimeoutRef.current);
    }
    
    redemptionSearchTimeoutRef.current = setTimeout(() => {
      handleRedemptionSearchSubmit();
    }, 2000);
    
    return () => {
      if (redemptionSearchTimeoutRef.current) {
        clearTimeout(redemptionSearchTimeoutRef.current);
      }
    };
  }, [redemptionSearchInput, handleRedemptionSearchSubmit]);
  
  // Handle approve with notification
  const handleApproveWithNotification = useCallback(async (requestId) => {
    const result = await handleApproveRequest(requestId);
    if (result) {
      showNotification(result.message, result.success ? 'success' : 'error');
    }
  }, [handleApproveRequest, showNotification]);
  
  // Role-based permissions
  const isFinance = currentRole === 'Finance';
  const isSupport = currentRole === 'Support';
  
  // Pagination calculations
  const redemptionTotalPages = Math.max(1, Math.ceil(redemptionPagination.total / redemptionPagination.items));
  
  // Access denied check
  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button 
            onClick={() => navigate('/subscriber-wallet')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-6xl space-y-6">
        {/* Back buttons */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => navigate('/subscriber-wallet')} 
              className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
            >
              ← Back to Home
            </button>
          <button 
            onClick={() => navigate('/subscriber-wallet/redemption-requests')} 
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Redemption Requests
          </button>
          </div>
        </div>
        
        {/* Subscriber Profile Card */}
        <RedemptionInfoCard
          profile={profile}
          showPhone={showPhone}
          setShowPhone={setShowPhone}
          isFinance={isFinance}
          isSupport={isSupport}
        />
        
        {/* Bank & Payout Details Card */}
        <BankDetailsCard
          bank={bank}
          showAccount={showAccount}
          setShowAccount={setShowAccount}
          isSupport={isSupport}
        />
        
        {/* Redemption History Table */}
        <RedemptionHistoryTable
          redemptions={redemptions}
          redemptionSearchInput={redemptionSearchInput}
          setRedemptionSearchInput={setRedemptionSearchInput}
          handleRedemptionSearchSubmit={handleRedemptionSearchSubmit}
          redemptionStatus={redemptionStatus}
          setRedemptionStatus={(val) => { setRedemptionStatus(val); setRedemptionPage(1); }}
          redemptionPage={redemptionPage}
          redemptionTotalPages={redemptionTotalPages}
          redemptionPagination={redemptionPagination}
          setRedemptionPage={setRedemptionPage}
          currentRole={currentRole}
          approvingRequests={approvingRequests}
          approvedRequestIds={approvedRequestIds}
          handleApproveRequest={handleApproveWithNotification}
        />
      </div>
      
      {/* Notification Toast */}
      <NotificationToast
        notification={notification}
        onClose={() => setNotification(null)}
        mounted={mounted}
      />
    </div>
  );
};

export default RedemptionSubscriberDetail;
